import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Input, Alert } from 'reactstrap';

import {
  colors,
  smallCapsStyle,
  inputStyle,
  headingStyle,
  maskSecret,
  lastChecked,
  connectionProblem,
  StepCard,
  StepWithAside,
  KeepConnectedBox,
  CopyField,
  FieldRow,
  Field,
  ResultList,
  ConnectedSummary,
  PrimaryButton,
  OutlineButton,
} from './ConnectionParts';

// WooCommerce: the business creates a Read/Write REST API key and gives Steppr the shop URL, Consumer key and
// Consumer secret. "Test and connect" calls wooTestAndSave, which saves only when the test passes, so the
// current details stay active until new ones work.

export const WOO_MESSAGES = {
  shopNotFound: "We couldn't find a WooCommerce shop at this address. Enter your shop's address as it opens in a browser (for example https://www.yourshop.co.uk) and check WooCommerce is installed and active.",
  permalinksPlain: 'Your WordPress permalinks are set to Plain, which hides the WooCommerce REST API. In WordPress go to Settings > Permalinks, choose any option except Plain (for example Post name), Save Changes, then try again.',
  wrongKey: "WooCommerce didn't accept this key. Copy the Consumer key (starts ck_) and Consumer secret (starts cs_) again, each into its own box, or add a new key in WooCommerce > Settings > Advanced > REST API.",
  readOnly: "This key can't create coupons: its permissions are not Read/Write. In WooCommerce > Settings > Advanced > REST API, edit the Steppr key, set Permissions to Read/Write and save, or add a new Read/Write key.",
  couponsDisabled: 'Coupons are turned off in your shop. In WooCommerce > Settings > General, tick "Enable the use of coupon codes", Save changes, then try again.',
  notAdmin: "This key belongs to a WordPress user who can't manage WooCommerce. Add a new key for an administrator account in WooCommerce > Settings > Advanced > REST API.",
  blocked: "Your shop's firewall or a security plugin blocked Steppr's request. Allow requests to your shop's /wp-json/wc/ address (for example in Cloudflare, Wordfence or your web host's security settings), then try again.",
  redirected: 'Your shop address forwards to a different address. Enter the exact address your shop opens on in a browser (with https:// and www. if it has them), then try again.',
  temporary: "Your shop didn't answer just now. Please try again in a minute.",
  notAllowed: "You don't have permission to change this business's connection.",
  missingData: 'Please fill in the shop URL, Consumer key and Consumer secret.',
};

const KEEP_CONNECTED = [
  'Turn off coupons in WooCommerce',
  'Revoke the Steppr key',
  'Delete the WordPress user the key belongs to, or remove its administrator role',
  "Let a security plugin or firewall block Steppr's requests to your shop",
];

export const wooResultLines = (res) => {
  if (!res) return [];
  const checks = res.checks || {};
  const fix = res.message || WOO_MESSAGES[res.reason] || WOO_MESSAGES.temporary;
  const steps = [
    { key: 'shopFound', ok: checks.shopFound, text: 'Shop found' },
    { key: 'keyAccepted', ok: checks.keyAccepted, text: 'Key accepted' },
    { key: 'readWrite', ok: checks.readWrite, text: 'Key has Read/Write permissions' },
    { key: 'couponsEnabled', ok: checks.couponsEnabled, text: 'Coupons turned on' },
  ];

  if (res.status === 'success') {
    return steps.map((s) => ({ key: s.key, ok: true, text: s.text }));
  }

  if (!res.checks) return [{ key: 'error', ok: false, text: fix }];

  const lines = [];
  for (let i = 0; i < steps.length; i += 1) {
    const s = steps[i];
    if (s.ok) {
      lines.push({ key: s.key, ok: true, text: s.text });
    } else {
      lines.push({ key: s.key, ok: false, text: fix });
      return lines;
    }
  }
  lines.push({ key: 'error', ok: false, text: fix });
  return lines;
};

// 'https://www.shop.co.uk/' -> 'www.shop.co.uk'
const displayUrl = (url) => String(url || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');

class WooConnection extends Component {
  static propTypes = {
    // connect({ shopUrl, consumerKey, consumerSecret }) -> Promise of the wooTestAndSave reply (never rejects)
    connect: PropTypes.func.isRequired,
    onConnected: PropTypes.func,
    // the saved connection ({ consumerKey, consumerSecret, woocommerceShopName }), or null
    existing: PropTypes.shape(),
    checkedAt: PropTypes.number,
    disabled: PropTypes.bool,
    // linked-shop form (partner shop of a multi-shop business): partner wording, own input ids, no summary
    partner: PropTypes.bool,
    // partner shop being edited: its saved shop URL (prefilled; key and secret are typed again)
    initialShopUrl: PropTypes.string,
    // checks run before the call (e.g. business name and email filled); returns a message or null
    validate: PropTypes.func,
  };

  static defaultProps = {
    onConnected: null,
    existing: null,
    checkedAt: null,
    disabled: false,
    partner: false,
    initialShopUrl: '',
    validate: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      editing: !(props.existing && props.existing.consumerKey),
      shopUrl: props.initialShopUrl || '',
      consumerKey: '',
      consumerSecret: '',
      busy: false,
      result: null,
      formError: null,
    };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate(prevProps) {
    const { existing } = this.props;
    const { busy } = this.state;
    const had = !!(prevProps.existing && prevProps.existing.consumerKey);
    const has = !!(existing && existing.consumerKey);

    if (!had && has && !busy) {
      this.setState({ editing: false });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleChange = (e) => {
    if (e && e.target && e.target.name) {
      const { name, value } = e.target;
      this.setState({ [name]: value, formError: null });
    }
  }

  startUpdate = () => {
    this.setState({ editing: true, shopUrl: '', consumerKey: '', consumerSecret: '', result: null, formError: null });
  }

  stopUpdate = () => {
    this.setState({ editing: false, shopUrl: '', consumerKey: '', consumerSecret: '', result: null, formError: null });
  }

  testAndConnect = () => {
    const { connect, onConnected, validate } = this.props;
    const { shopUrl, consumerKey, consumerSecret, busy } = this.state;

    if (busy) return null;

    if (validate) {
      const problem = validate();
      if (problem) {
        this.setState({ formError: problem, result: null });
        return null;
      }
    }

    const url = String(shopUrl || '').trim();
    const key = String(consumerKey || '').trim();
    const secret = String(consumerSecret || '').trim();

    if (!url || !key || !secret) {
      this.setState({ formError: WOO_MESSAGES.missingData, result: null });
      return null;
    }

    this.setState({ busy: true, result: null, formError: null });

    return Promise.resolve(connect({ shopUrl: url, consumerKey: key, consumerSecret: secret })).then((res) => {
      const reply = res || { status: 'error', reason: 'temporary' };
      if (!this.mounted) return;

      if (reply.status === 'success') {
        this.setState({ busy: false, result: reply, editing: false, shopUrl: '', consumerKey: '', consumerSecret: '' });
        if (onConnected) onConnected(reply);
      } else {
        this.setState({ busy: false, result: reply });
      }
    }).catch(() => {
      if (this.mounted) this.setState({ busy: false, result: { status: 'error', reason: 'temporary' } });
    });
  }

  renderSteps() {
    const { disabled, existing, partner, initialShopUrl } = this.props;
    const { shopUrl, consumerKey, consumerSecret, busy, result, formError } = this.state;
    const hasExisting = !!(existing && existing.consumerKey);
    const id = (name) => (partner ? `linkedWoo${name}` : `woo${name}`);

    return (
      <>
        <StepCard number={1}>
          <StepWithAside aside={<KeepConnectedBox items={KEEP_CONNECTED} />}>
            <h2 style={headingStyle}>{partner ? "Create a Steppr key in the partner's WooCommerce" : 'Create a Steppr key in WooCommerce'}</h2>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 16, lineHeight: 1.7 }}>
              <li>{partner ? "Log in to the partner shop's WordPress admin as an administrator (or ask the partner to do these steps)." : 'Log in to your WordPress admin as an administrator.'}</li>
              <li>Go to Settings, then Permalinks, and make sure any option other than Plain is selected (Day and name works well).</li>
              <li>Turn on coupons: go to WooCommerce, then Settings (the General tab opens). Scroll down to Enable coupons, tick Enable the use of coupon codes, and press Save changes.</li>
              <li>Go to WooCommerce, then Settings, then Advanced, then REST API, and press Add key.</li>
              <li>{partner ? 'Enter the description below, choose an administrator account as the user, and set permissions to Read/Write.' : 'Enter the description below, choose your administrator account as the user, and set permissions to Read/Write.'}</li>
              <li>Press Generate API key, then copy the Consumer key and Consumer secret straight away. The secret is only shown once.</li>
            </ol>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <CopyField label="Description" value="Steppr" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: colors.page, borderRadius: 10, padding: '8px 14px' }}>
                <span style={{ fontSize: 14, color: colors.muted }}>Permissions</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Read/Write</span>
              </div>
            </div>
          </StepWithAside>
        </StepCard>

        <StepCard number={2}>
          <h2 style={headingStyle}>{partner ? "Enter the partner shop's details" : "Enter your shop's details"}</h2>
          {(partner && initialShopUrl) ? (
            <p style={{ margin: 0, fontSize: 15, color: colors.muted }}>Enter the Consumer key and Consumer secret again (the saved ones aren't shown).</p>
          ) : null}
          <FieldRow>
            <Field id={id('ShopUrl')} label="Shop URL">
              <Input name="shopUrl" id={id('ShopUrl')} type="text" placeholder="https://yourshop.co.uk" autoComplete="off" value={shopUrl} style={inputStyle} onChange={this.handleChange} />
            </Field>
            <Field id={id('ConsumerKey')} label="Consumer key">
              <Input name="consumerKey" id={id('ConsumerKey')} type="text" placeholder="Starts with ck_" autoComplete="off" value={consumerKey} style={inputStyle} onChange={this.handleChange} />
            </Field>
            <Field id={id('ConsumerSecret')} label="Consumer secret">
              <Input name="consumerSecret" id={id('ConsumerSecret')} type="password" placeholder="Starts with cs_" autoComplete="new-password" value={consumerSecret} style={inputStyle} onChange={this.handleChange} />
            </Field>
          </FieldRow>
        </StepCard>

        <StepCard number={3}>
          <h2 style={headingStyle}>Test and connect</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <PrimaryButton onClick={this.testAndConnect} disabled={disabled} busy={busy}>
              {busy ? 'Testing' : 'Test and connect'}
            </PrimaryButton>
            <span style={{ fontSize: 14, color: colors.muted }}>Nothing is saved until the test passes.</span>
            {hasExisting ? (
              <OutlineButton onClick={this.stopUpdate} disabled={busy} style={{ marginLeft: 'auto' }}>Cancel</OutlineButton>
            ) : null}
          </div>
          {(hasExisting || (partner && initialShopUrl)) ? (
            <p style={{ margin: 0, fontSize: 14, color: colors.muted }}>{partner ? "The partner shop's current connection stays active until the new details pass the test." : 'Your current connection stays active until the new details pass the test.'}</p>
          ) : null}
          {formError ? (<Alert color="danger" style={{ margin: 0 }}>{formError}</Alert>) : null}
          <ResultList lines={wooResultLines(result)} />
        </StepCard>
      </>
    );
  }

  render() {
    const { existing, checkedAt, disabled, partner } = this.props;
    const { editing, result } = this.state;
    const hasExisting = !!(existing && existing.consumerKey);

    if (editing || !hasExisting) {
      return this.renderSteps();
    }

    if (partner) return null;

    const checked = lastChecked(checkedAt);
    const connected = lastChecked(existing.connectedAt);
    // A test that passed in this visit is newer than the stored status
    const problem = checkedAt ? null : connectionProblem(this.props.status);

    return (
      <>
        {(result && result.status === 'success') ? (
          <div style={{ marginBottom: 20 }}>
            <ResultList lines={wooResultLines(result)} />
          </div>
        ) : null}
        <p style={smallCapsStyle}>Your WooCommerce connection</p>
        <ConnectedSummary
          address={displayUrl(existing.woocommerceShopName)}
          secretText={existing.consumerSecret ? `Consumer secret ${maskSecret(existing.consumerSecret)}` : null}
          checkedText={checked ? `Last checked ${checked}` : (connected ? `Connected ${connected}` : null)}
          problem={problem}
          problemHelp="Your campaigns may be paused until this is fixed. Check your shop is online, coupons are enabled and the Steppr key still exists, or use Update details with a new key."
          buttonText="Update details"
          onButton={this.startUpdate}
          buttonDisabled={disabled}
        />
      </>
    );
  }
}

export default WooConnection;
